/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-preferences
 * @responsibility Compose independently tuned desktop and mobile SET-F03 Preferences pane surfaces.
 * @state-owner none; parent owns server state
 * @allowed-imports React; shared translator; @vispo/contracts types
 * @forbidden direct API client; display-name or avatar UI or copy
 * @public-api PreferencesSurface
 * @side-effects invokes parent commands only
 * @tests PreferencesSurface.test.tsx
 */

import type { Locale } from '@vispo/contracts';
import { useTranslator } from '../../../shared/model/use-workspace';
import './settings-preferences.css';

const LANGUAGE_OPTIONS: ReadonlyArray<{ label: string; value: Locale }> = [
  { label: '繁體中文', value: 'zh' },
  { label: 'English', value: 'en' },
];

export interface PreferencesSurfaceProps {
  errorCode: string | null;
  locale: Locale;
  mobile: boolean;
  onRetry(): void;
  onSelectLocale(locale: Locale): void;
  saving: boolean;
}

export function PreferencesSurface(props: PreferencesSurfaceProps) {
  const t = useTranslator();

  return (
    <section
      aria-label={t('偏好設定')}
      className={`preferences-pane${props.mobile ? ' preferences-pane--mobile' : ' preferences-pane--desktop'}`}
    >
      {props.errorCode ? (
        <div className="preferences-pane__error" role="alert">
          <span>{t('更新失敗，請重試')}</span>
          <button type="button" onClick={props.onRetry}>
            {t('重新整理')}
          </button>
        </div>
      ) : null}

      <div className="preferences-pane__section">
        <span className="preferences-pane__label" id="preferences-pane-language-label">
          {t('介面語言')}
        </span>
        <div
          aria-labelledby="preferences-pane-language-label"
          className="preferences-pane__language"
          role="radiogroup"
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <label
              className={`preferences-pane__language-option${
                option.value === props.locale ? ' is-selected' : ''
              }`}
              key={option.value}
            >
              <input
                checked={option.value === props.locale}
                disabled={props.saving}
                name="preferences-pane-language"
                type="radio"
                value={option.value}
                onChange={() => {
                  if (option.value !== props.locale) props.onSelectLocale(option.value);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}
